import ModalPortal from '@alltrails/core/components/ModalPortal';
import useScroll from '@alltrails/core/hooks/useScroll';
import { ComponentProps, forwardRef, HTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { FormattedMessage, useIntl } from 'react-intl';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import logMapLayerDrawerTabTapped from '@alltrails/amplitude/events/logMapLayerDrawerTabTapped';
import FocusTrap from 'focus-trap-react';
import classNames from 'classnames';
import { useMultiStepModal, Modal, MultiStepModal, ModalStep } from '@alltrails/modal';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import logTrailConditionsCTAViewed from '@alltrails/amplitude/events/logTrailConditionsCTAViewed';
import logCommunityHeatmapsCTAViewed from '@alltrails/amplitude/events/logCommunityHeatmapsCTAViewed';
import { useSelector } from '../../redux';
import { BaseStyleId, DynamicStyleId, StaticStyleId, StyleId } from '../../types/Styles';
import LayerMenuItem from '../LayerMenuItem';
import { LayerType } from '../../types/LayerMenu';
import ExtraMenuItem from '../ExtraMenuItem';
import filterHiddenStyles from './filterHiddenStyles';
import LayerControl from './LayerControl';
import LayerCards from './LayerCards';
import styles from './LayerMenu.module.scss';
import CustomURLModal from './CustomURLModal';
import HeatmapPrivacyModalContent from './HeatmapPrivacyModalContent';

// https://www.figma.com/design/vDdcMEqjmNXfQV7vvTtocS/Heatmaps-%5BHandoff%5D?node-id=1277-258156&p=f&m=dev

export const baseMapStyles: BaseStyleId[] = [
  'alltrailsOutdoorsV2', // AllTrails
  'alltrailsSatellite', //  Satellite
  'googleSatellite', //     Google satelllite   ** admin only **
  'worldparks', //          Terrain
  'roadmap', //             Road
  'openstreet', //          OpenStreetMap (OSM)
  'opencycle', //           OpenCycleMap (OCM)
  'topo', //                U.S. Geological Survey (USGS)
  'worldtopo', //           Terrain (legacy)    ** admin only **
  'adminBasemap' //         Custom basemap      ** admin only **
];

export const conditionsStyles: (StaticStyleId | DynamicStyleId)[] = ['precipitation', 'temperature', 'snowDepth', 'groundConditions'];

export const extraStyles: (StaticStyleId | DynamicStyleId)[] = [
  'heatmap', //             Heatmap             ** admin only **
  'photos', //              Photos
  'surfaceType', //         Surface type
  'waypoints', //           Waypoints
  'distanceMarkers', //     Distance markers
  'nearbyTrails', //        Nearby trails
  'geoBoundaries', //       Polygons            ** admin only **
  'recordingsAll', //       Recordings all      ** admin only **
  'recordingsPop', //       Recordings popular  ** admin only **
  'after', //               Last 60 days        ** admin only **
  'limit', //               Most recent 50      ** admin only **
  'pendingTrails', //       Pending trails      ** admin only **
  'mapsAll', //             Maps all            ** admin only **
  'waymarkedCycling', //    Waymarked cycling
  'waymarkedHiking', //     Waymarked hiking
  'waymarkedMTB', //        Waymarked MTB
  'adminOverlay', //        Custom overlay      ** admin only **
  'photospot' //            Photospot           ** admin only **
];

type Props = {
  isOpen: boolean;
  onCloseRequest: ComponentProps<typeof Modal>['onCloseRequest'];
  desktopModalProps: HTMLAttributes<HTMLDivElement>;
  mapLocation: MapLocation;
  pauseFocusTrap?: boolean;
} & Pick<
  ComponentProps<typeof LayerMenuItem>,
  'disableAnalytics' | 'handleFailedEntitlementCheck' | 'handleRoadblock' | 'onMapBaseLayerTapped' | 'onMapOverlayTapped'
>;

const LayerMenu = forwardRef<HTMLDivElement, Props>(
  (
    {
      isOpen,
      onCloseRequest,
      onMapBaseLayerTapped,
      onMapOverlayTapped,
      disableAnalytics,
      handleFailedEntitlementCheck,
      handleRoadblock,
      desktopModalProps,
      mapLocation,
      pauseFocusTrap
    },
    desktopModalRef
  ) => {
    const intl = useIntl();
    const isMobile = useIsMobileSizedScreen();
    const scrollContainer = useRef<HTMLDivElement>(null);
    const { hasPermission } = useAuthorization();
    const isAdmin = hasPermission({ permission: 'trails:manage' });
    const isPLP = hasPermission({ permission: 'public_lands:manage' });
    const ariaLabelElementId = 'layer-menu-modal-title';
    const { canScrollUp, updateScroll } = useScroll(scrollContainer);
    const { gpsiesUser, is3dActive, supportedDynamicStyleIds } = useSelector(state => ({
      gpsiesUser: state.context.gpsiesUser,
      is3dActive: state.map.is3dActive,
      supportedDynamicStyleIds: state.map.supportedDynamicStyleIds,
      dynamicStyleIds: state.map.dynamicStyleIds
    }));
    const isGpsiesUser = Boolean(gpsiesUser);
    const [adminStyleSourceUrlId, setAdminStyleSourceUrlId] = useState<StyleId | null>('adminBasemap');
    const { activeModal, goBack, navigateTo } = useMultiStepModal<'cards' | 'heatmap-privacy'>('cards');
    const [isCustomUrlModalOpen, setIsCustomUrlModalOpen] = useState(false);

    const renderCards = useCallback(
      (ids: StyleId[], shouldRenderDescription?: boolean): ReactNode => {
        return ids.length
          ? ids.map(id => (
              <LayerMenuItem
                key={`card-${id}`}
                styleId={id}
                onMapBaseLayerTapped={onMapBaseLayerTapped}
                onMapOverlayTapped={onMapOverlayTapped}
                disableAnalytics={disableAnalytics}
                shouldRenderDescription={shouldRenderDescription}
                onUrlButtonClick={() => setIsCustomUrlModalOpen(true)}
                handleFailedEntitlementCheck={handleFailedEntitlementCheck}
                handleRoadblock={handleRoadblock}
              />
            ))
          : null;
      },
      [onMapBaseLayerTapped, onMapOverlayTapped, disableAnalytics, handleFailedEntitlementCheck, handleRoadblock]
    );

    const renderExtraCards = useCallback(
      (ids: StyleId[]) => {
        return ids.map(id => (
          <ExtraMenuItem
            key={`card-${id}`}
            styleId={id}
            onMapBaseLayerTapped={onMapBaseLayerTapped}
            onMapOverlayTapped={onMapOverlayTapped}
            disableAnalytics={disableAnalytics}
            onUrlButtonClick={() => setIsCustomUrlModalOpen(true)}
            handleFailedEntitlementCheck={handleFailedEntitlementCheck}
            handleRoadblock={handleRoadblock}
          />
        ));
      },
      [onMapBaseLayerTapped, onMapOverlayTapped, disableAnalytics, handleFailedEntitlementCheck, handleRoadblock]
    );

    const baseMapCards = useMemo(
      () =>
        renderCards(
          filterHiddenStyles({
            ids: baseMapStyles,
            isAdmin,
            isPLP,
            isGpsiesUser,
            is3dActive,
            supportedDynamicStyleIds
          })
        ),
      [isAdmin, isPLP, isGpsiesUser, is3dActive, renderCards, supportedDynamicStyleIds]
    );

    const heatmapCard = useMemo(
      () =>
        renderCards(
          filterHiddenStyles({
            ids: ['newHeatmap'],
            isAdmin,
            isPLP,
            isGpsiesUser,
            is3dActive,
            supportedDynamicStyleIds
          }),
          true
        ),
      [is3dActive, isAdmin, isGpsiesUser, isPLP, renderCards, supportedDynamicStyleIds]
    );

    const conditionsCards = useMemo(() => {
      return renderCards(filterHiddenStyles({ ids: conditionsStyles, isAdmin, isPLP, isGpsiesUser, is3dActive, supportedDynamicStyleIds }), true);
    }, [renderCards, isAdmin, isPLP, isGpsiesUser, is3dActive, supportedDynamicStyleIds]);

    const extraCards = useMemo(() => {
      return renderExtraCards(
        filterHiddenStyles({
          ids: extraStyles,
          isAdmin,
          isPLP,
          isGpsiesUser,
          is3dActive,
          supportedDynamicStyleIds
        })
      );
    }, [renderExtraCards, isAdmin, isPLP, isGpsiesUser, is3dActive, supportedDynamicStyleIds]);

    const options = useMemo(
      () => [
        { id: 'baseMap' as LayerType, label: intl.formatMessage({ defaultMessage: 'Base map' }) },
        ...(!!heatmapCard || !!conditionsCards ? [{ id: 'overlays' as LayerType, label: intl.formatMessage({ defaultMessage: 'Overlays' }) }] : []),
        ...(extraCards.length > 0 ? [{ id: 'extras' as LayerType, label: intl.formatMessage({ defaultMessage: 'Extras' }) }] : [])
      ],
      [intl, heatmapCard, conditionsCards, extraCards.length]
    );

    const [layerType, setLayerType] = useState<LayerType>('baseMap');

    const hasLoggedOverlayViewEvents = useRef(false);
    useEffect(() => {
      if (layerType === 'overlays' && !hasLoggedOverlayViewEvents.current) {
        if (heatmapCard) {
          logCommunityHeatmapsCTAViewed({ map_location: mapLocation });
        }
        if (conditionsCards) {
          logTrailConditionsCTAViewed({ map_location: mapLocation });
        }
        hasLoggedOverlayViewEvents.current = true;
      }
    }, [conditionsCards, heatmapCard, layerType, mapLocation]);

    const onLayerTypeChange = useCallback(
      (newLayerType: LayerType) => {
        setLayerType(newLayerType);
        setAdminStyleSourceUrlId(newLayerType === 'baseMap' ? 'adminBasemap' : newLayerType === 'extras' ? 'adminOverlay' : null);
        if (!disableAnalytics) {
          logMapLayerDrawerTabTapped({
            map_location: mapLocation,
            to_map_tab: newLayerType === 'baseMap' ? 'base map' : newLayerType === 'overlays' ? 'overlays' : 'extras'
          });
        }
        if (scrollContainer.current) {
          scrollContainer.current.scrollTo?.({ top: 0 });
        }
      },
      [disableAnalytics, mapLocation]
    );

    return (
      <>
        {isMobile ? (
          <MultiStepModal
            isOpen={isOpen}
            ariaLabelElementId={ariaLabelElementId}
            modalClassName={styles.mobileMenuModal}
            isPaused={pauseFocusTrap}
            initialFocus={false}
            onBackRequest={activeModal === 'cards' ? undefined : goBack}
            onCloseRequest={onCloseRequest}
          >
            <div id={ariaLabelElementId} className={styles.visuallyHidden}>
              {activeModal === 'cards' ? <FormattedMessage defaultMessage="Layer menu" /> : <FormattedMessage defaultMessage="About heatmaps" />}
            </div>
            {activeModal === 'cards' ? (
              <ModalStep onCloseRequest={onCloseRequest} contentsClassName={styles.modalContent}>
                <LayerCards
                  layerType={layerType}
                  baseMapCards={baseMapCards}
                  conditionsCards={conditionsCards}
                  extraCards={extraCards}
                  heatmapCard={heatmapCard}
                  layerControl={<LayerControl layerType={layerType} options={options} onLayerTypeChange={onLayerTypeChange} />}
                  onHeatmapPrivacyIconClick={() => navigateTo('heatmap-privacy')}
                />
              </ModalStep>
            ) : (
              <ModalStep
                onCloseRequest={onCloseRequest}
                title={<FormattedMessage defaultMessage="About heatmaps" />}
                contentsClassName={styles.mobilePrivacyContent}
                buttons={{
                  primaryButton: {
                    onClick: () => navigateTo('cards'),
                    text: <FormattedMessage defaultMessage="Got it" />,
                    testId: 'heatmap-privacy-modal-got-it'
                  }
                }}
              >
                <div className={styles.heatmapPrivacyContent}>
                  <HeatmapPrivacyModalContent />
                </div>
              </ModalStep>
            )}
          </MultiStepModal>
        ) : (
          <ModalPortal>
            <FocusTrap
              active={isOpen}
              focusTrapOptions={{
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: onCloseRequest,
                // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
                fallbackFocus: '#layer-menu-container'
              }}
              paused={pauseFocusTrap}
            >
              <div
                ref={desktopModalRef}
                className={styles.layerMenuModal}
                aria-labelledby={ariaLabelElementId}
                role="dialog"
                id="layer-menu-container"
                data-at-theme-dimensions="default"
                {...desktopModalProps}
              >
                <div id={ariaLabelElementId} className={styles.visuallyHidden}>
                  <FormattedMessage defaultMessage="Layer menu" />
                </div>
                {options?.length > 1 && (
                  <div className={classNames(styles.header, { [styles.bottomBorder]: canScrollUp })}>
                    <LayerControl layerType={layerType} options={options} onLayerTypeChange={onLayerTypeChange} />
                  </div>
                )}
                <div className={styles.scrollContainer} onScroll={updateScroll} ref={scrollContainer} id="modal-scroll-container">
                  <div
                    className={classNames(styles.modalContent, {
                      [styles.morePadding]: options.length <= 1
                    })}
                  >
                    <LayerCards
                      layerType={layerType}
                      baseMapCards={baseMapCards}
                      conditionsCards={conditionsCards}
                      extraCards={extraCards}
                      heatmapCard={heatmapCard}
                      layerControl={<LayerControl layerType={layerType} options={options} onLayerTypeChange={onLayerTypeChange} />}
                      onHeatmapPrivacyIconClick={() => navigateTo('heatmap-privacy')}
                    />
                  </div>
                </div>
              </div>
            </FocusTrap>
          </ModalPortal>
        )}
        {isCustomUrlModalOpen && adminStyleSourceUrlId && (
          <CustomURLModal
            isOpen={isCustomUrlModalOpen}
            adminStyleSourceUrlId={adminStyleSourceUrlId}
            onClose={() => setIsCustomUrlModalOpen(false)}
          />
        )}
      </>
    );
  }
);

export default LayerMenu;
